<template>
  <section class="admin-stats">
    <div class="stats-header">
      <h2>Køkkenoversigt</h2>
      <p class="stats-subtitle">{{ encouragement }}</p>
    </div>

    <div class="stats-grid">
      <!-- Total recipes (big "hero" stat) -->
      <div class="stat-card stat-hero">
        <div class="stat-icon"><fa icon="book" /></div>
        <div class="stat-value">{{ stats.total }}</div>
        <div class="stat-label">Opskrifter i alt</div>
        <div class="stat-sub" v-if="stats.addedThisMonth > 0">
          +{{ stats.addedThisMonth }} denne måned
        </div>
      </div>

      <!-- Done -->
      <div class="stat-card stat-done">
        <div class="stat-icon"><fa icon="check-circle" /></div>
        <div class="stat-value">{{ stats.done }}</div>
        <div class="stat-label">Lavet</div>
        <div class="stat-progress">
          <div class="stat-progress-track">
            <div class="stat-progress-fill" :style="{ width: stats.donePct + '%' }"></div>
          </div>
          <span class="stat-progress-label">{{ stats.donePct }}%</span>
        </div>
      </div>

      <!-- Favorites -->
      <div class="stat-card stat-fav">
        <div class="stat-icon"><fa icon="star" /></div>
        <div class="stat-value">{{ stats.favorites }}</div>
        <div class="stat-label">Favoritter</div>
        <div class="stat-sub" v-if="stats.total > 0">
          {{ stats.favoritePct }}% af samlingen
        </div>
      </div>

      <!-- Categories breadth -->
      <div class="stat-card stat-cats">
        <div class="stat-icon"><fa icon="layer-group" /></div>
        <div class="stat-value">{{ stats.categories }}</div>
        <div class="stat-label">Kategorier</div>
        <div class="stat-sub" v-if="stats.topCategory">
          flest i <strong>{{ stats.topCategory.name }}</strong>
        </div>
      </div>

      <!-- Cooking time average -->
      <div class="stat-card stat-time">
        <div class="stat-icon"><fa icon="clock" /></div>
        <div class="stat-value">{{ stats.avgTime || '—' }}</div>
        <div class="stat-label">Gennemsnit min</div>
        <div class="stat-sub" v-if="stats.withTime > 0">
          baseret på {{ stats.withTime }} opskrifter
        </div>
      </div>

      <!-- Next-up nudge -->
      <div class="stat-card stat-todo">
        <div class="stat-icon"><fa icon="utensils" /></div>
        <div class="stat-value">{{ stats.unmadeFavorites }}</div>
        <div class="stat-label">Favoritter at lave</div>
        <div class="stat-sub" v-if="stats.unmadeFavorites === 0 && stats.favorites > 0">
          alt lavet — godt gået!
        </div>
        <div class="stat-sub" v-else-if="stats.unmadeFavorites > 0">
          venter i køen
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'AdminStats',
  computed: {
    ...mapGetters(['allRecipes']),
    stats() {
      const recipes = this.allRecipes || []
      const total = recipes.length
      const done = recipes.filter(r => r.Done).length
      const favorites = recipes.filter(r => r.Favorite).length
      const unmadeFavorites = recipes.filter(r => r.Favorite && !r.Done).length

      // Recipes added this month
      const now = new Date()
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)
      const addedThisMonth = recipes.filter(r => {
        if (!r.Created) return false
        const d = new Date(r.Created)
        return !isNaN(d) && d >= monthStart
      }).length

      // Categories breadth + top category
      const catCounts = {}
      recipes.forEach(r => {
        if (!Array.isArray(r.Categories)) return
        const seen = new Set()
        r.Categories.forEach(c => {
          const name = c && (c.Parent || c.Name)
          if (!name || seen.has(name)) return
          seen.add(name)
          catCounts[name] = (catCounts[name] || 0) + 1
        })
      })
      const categories = Object.keys(catCounts).length
      let topCategory = null
      for (const name in catCounts) {
        if (!topCategory || catCounts[name] > topCategory.count) {
          topCategory = { name, count: catCounts[name] }
        }
      }

      // Average cooking time
      const withTimeArr = recipes
        .map(r => parseInt(r.CookingTime, 10))
        .filter(n => !isNaN(n) && n > 0)
      const withTime = withTimeArr.length
      const avgTime = withTime > 0
        ? Math.round(withTimeArr.reduce((a, b) => a + b, 0) / withTime)
        : 0

      return {
        total,
        done,
        donePct: total > 0 ? Math.round((done / total) * 100) : 0,
        favorites,
        favoritePct: total > 0 ? Math.round((favorites / total) * 100) : 0,
        unmadeFavorites,
        addedThisMonth,
        categories,
        topCategory,
        avgTime,
        withTime
      }
    },
    encouragement() {
      const { total, done, donePct, addedThisMonth, unmadeFavorites } = this.stats
      if (total === 0) return 'Tilføj din første opskrift for at komme i gang.'
      if (donePct >= 90) return `Imponerende — du har lavet ${donePct}% af dine opskrifter.`
      if (addedThisMonth >= 3) return `Du har tilføjet ${addedThisMonth} opskrifter denne måned. Bliv ved!`
      if (unmadeFavorites > 0) return `Du har ${unmadeFavorites} favoritter, du endnu ikke har lavet.`
      if (done > 0) return `Du har lavet ${done} ud af ${total} opskrifter — godt arbejde.`
      return 'Tilføj og lav opskrifter for at fylde din samling op.'
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-stats {
  margin-top: 65px; // sit below the fixed nav
  // No external margin: the ResultsBar that follows owns its own 10px
  // blurred top padding which is the visible gap.
  margin-bottom: 0;
  background: linear-gradient(135deg, var(--dark) 0%, var(--darker) 60%, var(--main) 100%);
  // Spacious + symmetric: equal top and bottom breathing inside the panel.
  // Horizontal gutter lives on the inner wrappers so the effective content
  // width matches .container (1240px max).
  padding: 50px 0 50px;
  color: white;
}

.stats-header,
.stats-grid {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;
}

.stats-header {
  margin-bottom: 24px;

  h2 {
    color: white;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    margin: 0 0 4px;
  }
}

.stats-subtitle {
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  font-size: 0.95rem;
  font-weight: 400;
  letter-spacing: 0.02em;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 14px;

  @media (max-width: 1100px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 700px)  { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 420px)  { grid-template-columns: 1fr; }
}

.stat-card {
  position: relative;
  background: rgba(255, 255, 255, 0.96);
  border-radius: 12px;
  padding: 20px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  }

  &.stat-hero {
    grid-column: span 2;
    background: linear-gradient(135deg, #fff8e7 0%, #fff 60%);
    border-top: 3px solid var(--gold);

    .stat-value { font-size: 3rem; color: var(--dark); }

    @media (max-width: 1100px) { grid-column: span 3; }
    @media (max-width: 700px)  { grid-column: span 2; }
    @media (max-width: 420px)  { grid-column: span 1; }
  }
}

.stat-icon {
  position: absolute;
  top: 14px;
  right: 14px;
  font-size: 1.4rem;
  opacity: 0.18;
  color: var(--dark);
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--darker);
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}

.stat-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--dark);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.85;
}

.stat-sub {
  font-size: 0.78rem;
  color: #666;
  margin-top: 6px;

  strong {
    color: var(--dark);
    font-weight: 600;
  }
}

.stat-progress {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.stat-progress-track {
  flex: 1;
  height: 6px;
  background: rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  overflow: hidden;
}

.stat-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, var(--main), var(--darker));
  border-radius: 999px;
  transition: width 0.4s ease;
}

.stat-progress-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--dark);
  font-variant-numeric: tabular-nums;
  min-width: 32px;
  text-align: right;
}

// Accent stripes
.stat-done { border-top: 3px solid var(--main); }
.stat-fav  { border-top: 3px solid var(--gold); }
.stat-cats { border-top: 3px solid #b8d4a8; }
.stat-time { border-top: 3px solid #94c084; }
.stat-todo { border-top: 3px solid #d4b35e; }
</style>
